// src/pages/Settings.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./Settings.css";
import InstallButton from "../InstallButton";
import { apiFetch } from "../lib/api";
import Banned from "../banned"; 

const USERNAME_RE = /^[a-zA-Z0-9_.-]{3,20}$/;

export default function Settings({
  user,
  userCountry,
  onLogout,
  autoSearchNext,
  setAutoSearchNext,
  openSignIn,
}) {
  const isLoggedOut = !user;
  const isGuest = !!user && !!user.isAnonymous;
  const isAuthed = !!user && !user.isAnonymous;

  const [username, setUsername] = useState("");
  const [usernameOk, setUsernameOk] = useState(true);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [cooldown, setCooldown] = useState({ allowed: true, nextAt: null, secondsRemaining: 0 });

  // ✅ 2. ADD BAN STATE
  const [isBanned, setIsBanned] = useState(false);
  const [banExpiresAt, setBanExpiresAt] = useState(null);
  const [banReason, setBanReason] = useState(null);
  const [showBannedPage, setShowBannedPage] = useState(false);

  const [meStats, setMeStats] = useState({
    points_all_time: 0,
    wins: 0,
    losses: 0,
    streak: 0,
  });

  const [txLoading, setTxLoading] = useState(false);
  const [txErr, setTxErr] = useState(null);
  const [transactions, setTransactions] = useState([]);

  // Collapsible Points Stream
  const [txOpen, setTxOpen] = useState(false);

  // ✅ ADDED: State to hold the dynamic country
  const [localCountry, setLocalCountry] = useState(userCountry || "Unknown");

  const authHeader = useMemo(() => {
    return async () => {
      if (!user) return {};
      const token = await user.getIdToken(true);
      return { Authorization: `Bearer ${token}` };
    };
  }, [user]);


  /* ------------------------------ Load profile ------------------------------ */
  // 🟢 FIX: Synchronous wrapper to prevent React crash
  const loadMe = useCallback(() => {
    if (!user) {
      setLoadingProfile(false);
      return;
    }

    let cancelled = false;

    // Async logic moved inside
    const fetchProfile = async () => {
      try {
        setLoadingProfile(true); 
        const headers = await authHeader();
        const data = await apiFetch(`/api/me?t=${Date.now()}`, { headers });
        
        if (cancelled) return;

        if (isAuthed) {
          setUsername(data?.username || "");
          setUsernameOk(true);
        }
        
        // ✅ ADDED: Capture the real country from the database
        if (data?.country) {
          setLocalCountry(data.country);
        }
        
        setMeStats({
          points_all_time: Number(data?.points_all_time || 0),
          wins: Number(data?.wins || 0),
          losses: Number(data?.losses || 0),
          streak: Number(data?.streak || 0),
          strikeCount: Number(data?.strikeCount || 0),
        });

        setCooldown({
          allowed: !!data?.usernameChangeAvailable,
          nextAt: data?.nextUsernameChangeAt || null,
          secondsRemaining: 0,
        });

        const currentlyBanned = !!data?.isBanned;
        setIsBanned(currentlyBanned);
        setBanExpiresAt(data?.banExpiresAt || null);
        setBanReason(data?.banReason || null);

        if (!currentlyBanned) {
          setShowBannedPage(false);
        }

      } catch (e) {
        console.error("Load profile failed:", e);
      } finally {
        if (!cancelled) setLoadingProfile(false); 
      }
    };

    fetchProfile();

    // 🟢 Returns a clean function
    return () => { cancelled = true; };
  }, [user, isAuthed, authHeader]);

  useEffect(() => {
    const cleanup = loadMe();
    return cleanup;
  }, [loadMe]);
 
  /* --------------------------- Load points stream --------------------------- */
  const loadTransactions = useCallback(async () => {
    if (!isAuthed) return;
    try {
      setTxLoading(true);
      setTxErr(null);
      const headers = await authHeader();
      const data = await apiFetch("/api/me/transactions?limit=50", { headers });
      setTransactions(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setTxErr(e?.message || "Failed to load points stream");
    } finally {
      setTxLoading(false);
    }
  }, [isAuthed, authHeader]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  // Auto-collapse if many items; auto-expand if few
  useEffect(() => {
    if (transactions.length > 15) setTxOpen(false);
    else if (transactions.length > 0) setTxOpen(true);
  }, [transactions.length]);

  /* ------------------------------ Username save ----------------------------- */
  function onUsernameChange(v) {
    setUsername(v);
    setSaveMsg(null);
    setUsernameOk(!v || USERNAME_RE.test(v));
  }

  async function saveUsername() {
    if (!isAuthed || !username) return;
    if (!USERNAME_RE.test(username)) {
      setUsernameOk(false);
      setSaveMsg("Invalid username (3–20, letters/numbers/_/./-)");
      return;
    }

    setSaving(true);
    setSaveMsg(null);

    try {
      const headers = {
        "Content-Type": "application/json",
        ...(await authHeader()),
      };

      await apiFetch("/api/me", {
        method: "PATCH",
        headers,
        body: JSON.stringify({ username: username.trim() }),
      });

      setSaveMsg("Saved ✓");
      await loadMe();
    } catch (e) {
      console.warn("Save username failed:", e);
      let msg = e?.message || "Failed to save username";

      if (e?.isApiError) {
        const s = e.status;
        const r = e.reason || "";
        if (s === 400 || s === 422) msg = "Invalid username (3–20, letters/numbers/_/./-)";
        else if (s === 401) msg = "Please sign in again.";
        else if (s === 402) msg = r || "Subscription or payment required to change username.";
        else if (s === 403) msg = "You don't have permission to change this username.";
        else if (s === 409) msg = "Username already taken.";
        else if (s === 429) {
          const ends = e.details?.cooldownEndsAt ? new Date(e.details.cooldownEndsAt) : null;
          const sec = typeof e.details?.secondsRemaining === "number" ? e.details.secondsRemaining : null;
          msg =
            "Username can be changed once every 7 days" +
            (ends ? ` · next: ${ends.toLocaleString()}` : "") +
            (sec != null ? ` · ${sec}s remaining` : "");
        } else if (r) {
          msg = r;
        }
      }

      setSaveMsg(msg);
    } finally {
      setSaving(false);
    }
  }

  // ✅ 6. ADD UNIVERSAL LOGOUT HANDLER
  const handleLogoutClick = async () => {
    if (loadingProfile) return;

    // 🟢 THE FIX: Do one last "emergency" check before showing the ban page
    if (isBanned) {
      await loadMe(); // Refresh data one last time
      if (isBanned) {
        setShowBannedPage(true);
        return;
      }
    }
    
    // If we get here, they are definitely not banned
    onLogout();
  };

  const handleSignInClick = async () => {
    if (loadingProfile) return;

    if (isBanned) {
      await loadMe(); // Refresh data
      if (isBanned) {
        setShowBannedPage(true);
        return;
      }
    }
    
    openSignIn();
  };

  // ✅ 8. RENDER BANNED PAGE IF TRIGGERED
  if (showBannedPage) {
    return <Banned banExpiresAt={banExpiresAt} reason={banReason} appealCode={user?.uid} />;
  }

  return (
    <div className="main-content-Setting">
      <div className="settings-card">
        <h2>Settings</h2>

        {/* Scrollable content area */}
        <div className="settings-content">
          {/* --- Account section --- */}
          <section className="pref-block">
            <h3 className="pref-title">Account</h3>

            {isLoggedOut && (
              <>
                <p>You're not signed in.</p>
                <button className="btn btn-primary" onClick={handleSignInClick}>
                  Sign in
                </button>
              </>
            )}

          {isGuest && (
              <>
                <p>You are currently using a temporary guest session. Connect an account to save your identity, track your stats, and secure your place on the leaderboard!</p>
                <div className="form-row">
                  <button className="btn btn-primary" onClick={handleSignInClick} style={{ width: '100%', padding: '12px', fontSize: '1.1rem' }}>
                    Sign in / Create account
                  </button>
                </div>
              </>
            )}

            {isAuthed && (
              <>
                <p>
                  Signed in as{" "}
                  <strong>{user.displayName || user.email || "Your account"}</strong>
                  {localCountry && localCountry !== "Unknown" ? ` — ${localCountry}` : null}
                </p>
                
                {/* ✅ 5. & 7. FIX AUTHED LOGOUT BUTTON */}
                <button
                  className="btn btn-secondary"
                  onClick={handleLogoutClick}
                  disabled={loadingProfile}
                >
                  {loadingProfile ? "Loading..." : "Logout"}
                </button>

                {/* --- Username editor --- */}
                <div className="pref-subblock">
                  <label htmlFor="username" className="pref-title">
                    Username
                  </label>
                  <div className="form-row">
                    <input
                      id="username"
                      className="form-control"
                      placeholder="your_name"
                      value={username}
                      onChange={(e) => onUsernameChange(e.target.value)}
                      disabled={loadingProfile || saving}
                    />
                    <button
                      className="btn btn-primary"
                      onClick={saveUsername}
                      disabled={loadingProfile || saving || !username || !usernameOk}
                      title="3–20 characters; letters, numbers, _, . and -"
                    >
                      {saving ? "Saving…" : "Save"}
                    </button>
                  </div>
                  <p className="pref-help">
                    3–20 characters. Allowed: letters, numbers, <code>_ . -</code>.{" "}
                    Usernames can be changed once every 7 days.
                  </p>
                  {!usernameOk && (
                    <div className="error-message">
                      Invalid username (3–20, letters/numbers/_/./-)
                    </div>
                  )}
                  {saveMsg && (
                    <div className={/saved|✓/i.test(saveMsg) ? "success-message" : "error-message"}>
                      {saveMsg}
                    </div>
                  )}
                  {!cooldown.allowed && cooldown.nextAt && (
                    <div className="cooldown-info">
                      Next change available: {new Date(cooldown.nextAt).toLocaleString()}
                    </div>
                  )}
                </div>

                {/* --- Quick stats --- */}
                <div className="pref-subblock">
                  <div className="stats-grid">
                    <Stat label="Points" value={meStats.points_all_time} />
                    <Stat label="Wins" value={meStats.wins} />
                    <Stat label="Losses" value={meStats.losses} />
                    <Stat label="Streak" value={meStats.streak} />
                  </div>
                </div>
              </>
            )}
          </section>

          {/* --- Matching preferences (moved up) --- */}
          <section className="pref-block">
            <h3 className="pref-title">Matching</h3>

            <label className="toggle-row" htmlFor="auto-search-next">
              <input
                id="auto-search-next"
                type="checkbox"
                checked={!!autoSearchNext}
                onChange={(e) => setAutoSearchNext(e.target.checked)}
              />
              <span className="toggle-label">Auto-search after a disconnect</span>
            </label>

            <p className="pref-help">
              If your chat partner leaves, we'll automatically start looking for a new
              match. No interruptions or notifications.
            </p>
          </section>

          {/* --- Connection info (moved up) --- */}
          <div className="connection-info">
            <h3>Connection Information</h3>

            {user ? (
              user.isAnonymous ? (
                <>
                  <p><strong>Login Type:</strong> Guest Login</p>
                  <p><strong>Country:</strong> {localCountry}</p>
                </>
              ) : (
                <>
                  <p>
                    <strong>Login Type:</strong>{" "}
                    {user.providerData && user.providerData[0] && user.providerData[0].providerId
                      ? user.providerData[0].providerId
                      : "Standard"}
                  </p>
                  <p><strong>Name:</strong> {user.displayName || "—"}</p>
                  <p><strong>Email:</strong> {user.email || "—"}</p>
                  <p><strong>Country:</strong> {localCountry}</p>
                </>
              )
            ) : (
              <p>No user information available.</p>
            )}
          </div>

          {/* --- Points Stream (moved to bottom, collapsible) --- */}
          {isAuthed && (
            <section className="pref-block" id="points-stream">
              <div className="pref-title-row">
                <h3 className="pref-title" style={{ marginBottom: 0 }}>Points stream</h3>
                {!txLoading && !txErr && transactions.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={() => setTxOpen((v) => !v)}
                    aria-expanded={txOpen ? "true" : "false"}
                    aria-controls="points-stream-list"
                  >
                    {txOpen ? "Collapse" : `Show all (${transactions.length})`}
                  </button>
                )}
              </div>

              {txErr && (
                <div className="error-message">
                  {txErr}{" "}
                  <button onClick={loadTransactions} className="btn-link">
                    Retry
                  </button>
                </div>
              )}

              {txLoading && <TxSkeleton count={4} />}

              {!txLoading && !txErr && transactions.length === 0 && (
                <div className="empty-state">No recent transactions.</div>
              )}

              {!txLoading && !txErr && transactions.length > 0 && (() => {
                const COLLAPSED_COUNT = 10;
                const visible = txOpen ? transactions : transactions.slice(0, COLLAPSED_COUNT);

                return (
                  <>
                    {!txOpen && transactions.length > COLLAPSED_COUNT && (
                      <div className="pref-help">
                        Showing the latest {COLLAPSED_COUNT} of {transactions.length} transactions.{" "}
                        <button className="btn btn-link" onClick={() => setTxOpen(true)}>
                          Show all
                        </button>
                      </div>
                    )}

                    <div
                      id="points-stream-list"
                      className={txOpen ? "points-scroll" : ""}
                      role="region"
                      aria-label="Points stream list"
                    >
                      <ul className="transactions-list">
                        {visible.map((t) => (
                          <TransactionItem key={t.id} transaction={t} />
                        ))}
                      </ul>
                    </div>

                    {!txOpen && transactions.length > COLLAPSED_COUNT && (
                      <div className="pref-help" style={{ marginTop: "0.75rem" }}>
                        Want to see more?{" "}
                        <button className="btn btn-link" onClick={() => setTxOpen(true)}>
                          Show all {transactions.length}
                        </button>
                      </div>
                    )}
                  </>
                );
              })()}
            </section>
          )}
        </div>
      </div>

      {/* PWA install button (already present) */}
      <InstallButton />
    </div>
  );
}

/* --------------------------------- UI ------------------------------------- */

function Stat({ label, value }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value ?? 0}</div>
    </div>
  );
}

function TxSkeleton({ count = 4 }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="tx-skeleton" />
      ))}
    </>
  );
}

function TransactionItem({ transaction }) {
  const isPositive = transaction.delta > 0;
  
  return (
    <li className={`transaction-item ${isPositive ? '' : 'negative'}`}>
      <div className="transaction-info">
        <div className="transaction-reason">
          {formatReason(transaction.reason)}
          {transaction.gameType && <span className="transaction-game-type"> · {transaction.gameType}</span>}
        </div>
        <div className="transaction-meta">
          {new Date(transaction.createdAt).toLocaleString()}
          {transaction.matchId && <span> · match {shortId(transaction.matchId)}</span>}
        </div>
      </div>
      <div className="transaction-points">
        <div className="transaction-delta">
          {transaction.delta > 0 ? `+${transaction.delta}` : transaction.delta} pts
        </div>
        {typeof transaction.totalAfter === "number" && (
          <div className="transaction-total">{transaction.totalAfter} total</div>
        )}
      </div>
    </li>
  );
}

/* --------------------------- tiny utilities -------------------------------- */

function shortId(s) {
  if (!s) return "id";
  const str = String(s);
  return str.length <= 8 ? str : str.slice(0, 4) + "…" + str.slice(-3);
}

function formatReason(r) {
  if (!r) return "award";
  const clean = String(r).replace(/_/g, " ").toLowerCase();
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}